import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ApiError } from '@mads/shared'
import { useIsOnline } from '@/lib/connectivity'

/**
 * Offline, a query with nothing saved for it does not fail — it waits, and goes
 * on waiting until there is a connection (issue #64). Said plainly here rather
 * than at each of twenty call sites: not every screen is kept for use
 * underground, and one that is not should say so instead of spinning for as
 * long as somebody is prepared to watch it.
 *
 * Screens that *do* have their data render it and never reach this, so the
 * sentence only ever appears where it is true.
 */
export function Loading({ what = 'Loading' }: { what?: string }) {
  const online = useIsOnline()

  if (!online) {
    return (
      <p className="spinner-note" role="status">
        Offline — this screen has not been saved for use without a connection.
      </p>
    )
  }

  return (
    <p className="spinner-note" role="status">
      {what}…
    </p>
  )
}

export function ErrorNote({ error }: { error: unknown }) {
  if (error === null || error === undefined) return null

  const message =
    error instanceof ApiError || error instanceof Error
      ? error.message
      : 'Something went wrong.'

  const violations = error instanceof ApiError ? Object.entries(error.violations) : []

  return (
    <div className="error-note" role="alert">
      {message}
      {violations.length > 0 && (
        <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: '1.1em' }}>
          {violations.map(([field, detail]) => (
            <li key={field}>
              {field}: {detail}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** A destructive action always asks first (spec W-03). */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  busy = false,
  children,
}: {
  title: string
  body: string
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
  busy?: boolean
  /**
   * Anything the confirmation needs beyond agreeing to it — the current
   * password, where the action is one that weakens the account (issue #15).
   * Rendered under the body so those cases still go through this dialog rather
   * than growing a second kind of confirmation beside it.
   */
  children?: ReactNode
}) {
  // The dialog mounts the moment a destructive button is clicked, so the
  // trigger is still the focused element then — capture it once so focus can
  // go back there when the dialog closes. Focus moves to the confirm button so
  // the action is one Enter away, and Escape cancels (the same as the backdrop
  // click and the Cancel button), except while a request is in flight.
  const confirmRef = useRef<HTMLButtonElement>(null)
  const [trigger] = useState<HTMLElement | null>(() => document.activeElement as HTMLElement | null)

  useEffect(() => {
    confirmRef.current?.focus()
    return () => {
      trigger?.focus()
    }
  }, [trigger])

  return (
    <div className="dialog-backdrop" onClick={onCancel}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !busy) {
            onCancel()
          }
        }}
      >
        <div className="dialog-title">{title}</div>
        <div className="dialog-body">{body}</div>
        {children}
        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            ref={confirmRef}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
